// GIF picker — reuses recognition's Giphy proxy (rec.searchGiphy) so there's one GIF
// provider/integration in the app. Picking sends the GIF as a message.
import React, { useEffect, useState } from 'react';
import * as rec from '@/services/recognitionService';
import type { MessageGif } from '../types';

export const GifPicker: React.FC<{ onPick: (gif: MessageGif) => void; onClose: () => void }> = ({ onPick, onClose }) => {
  const [query, setQuery] = useState('');
  const [gifs, setGifs] = useState<rec.Gif[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const id = setTimeout(() => {
      rec
        .searchGiphy(query.trim())
        .then(setGifs)
        .catch(() => setGifs([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(id);
  }, [query]);

  return (
    <div className="absolute bottom-full left-0 z-20 mb-2 w-80 rounded-xl border border-border bg-card p-2 shadow-lg">
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
        placeholder="Search GIFs…"
        className="mb-2 w-full rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm text-charcoal placeholder:text-muted-foreground focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
      />
      {loading ? (
        <div className="py-6 text-center text-xs text-muted-foreground">Loading…</div>
      ) : gifs.length === 0 ? (
        <div className="py-6 text-center text-xs text-muted-foreground">No GIFs — try another search.</div>
      ) : (
        <div className="grid max-h-64 grid-cols-2 gap-1 overflow-y-auto">
          {gifs.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => {
                onPick({ url: g.url, preview: g.preview, title: g.title });
                onClose();
              }}
              className="overflow-hidden rounded-md border border-border transition-colors hover:border-primary-400"
            >
              <img src={g.preview || g.url} alt={g.title || 'GIF'} loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="mt-1 text-right text-[10px] text-muted-foreground">Powered by GIPHY</div>
    </div>
  );
};
